import * as React from "react";

import { RobotoLLMContext } from "@/shared/domain/chat/ChatRecord";

import { ContextType } from "./contextType";
import {
  ContextDisplayNames,
  useRobotoLLMContext,
} from "./hooks/useRobotoLLMContext";

const CHAT_ID_STORAGE_KEY = "roboto-ai-chat-current-chat-id";

/**
 * AIChatContext provides UI state only - no data fetching or mutations.
 * Data fetching and mutations are handled by the Chat component.
 */
interface AIChatContextValue {
  // UI state
  isFullscreen: boolean;
  showHistory: boolean;
  autoEnabled: boolean;
  currentChatId: string | undefined;
  context: RobotoLLMContext;
  contextDisplayNames: ContextDisplayNames;
  modelProfile: string | undefined;

  // UI state setters
  setIsFullscreen: (value: boolean) => void;
  setShowHistory: (value: boolean) => void;
  setAutoEnabled: (value: boolean) => void;
  setCurrentChatId: (chatId: string | undefined) => void;
  setModelProfile: (profile: string) => void;

  // Convenience handlers
  handleContextDelete: (
    contextType: ContextType,
    contextContent: string,
  ) => void;
  handleNewChat: () => void;
  handleToggleFullscreen: () => void;
  handleToggleHistory: () => void;
  handleToggleAuto: () => void;
  handleHistoryChatClicked: (chatId: string) => void;
  handleLinkClick: () => void;
  handleResetUIState: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AIChatContext = React.createContext<
  AIChatContextValue | undefined
>(undefined);

interface AIChatProviderProps {
  children: React.ReactNode;
  /** Optional initial chat id. When set, the provider starts on that chat
   *  and skips the sessionStorage read AND the sessionStorage write — the
   *  caller (e.g. the admin chat explorer) owns its own chat selection and
   *  must not clobber the regular sidebar's last-active chat. The lazy
   *  initializer reads ``initialChatId`` once at mount; remount with a new
   *  ``key`` if the caller needs to swap chats from outside. */
  initialChatId?: string;
  /** Fires whenever the active chat id changes. Lets parent wrappers keep
   *  their own state (e.g. a selected-chat id) in sync with forks and
   *  history-pane clicks. The handler is invoked via a ref so passing an
   *  inline arrow doesn't restart the effect on every render. */
  onChatIdChanged?: (chatId: string | undefined) => void;
}

export const AIChatProvider: React.FC<AIChatProviderProps> = ({
  children,
  initialChatId,
  onChatIdChanged,
}) => {
  // ``initialChatId`` is the signal that this provider is being used outside
  // the regular sidebar (today: only ``AdminChatViewer``). When set, we skip
  // both the sessionStorage read and the sessionStorage write so an admin
  // poking around in the explorer never overwrites the user's last-active
  // sidebar chat.
  const ownsSessionStorage = initialChatId === undefined;
  // UI state
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [showHistory, setShowHistory] = React.useState(false);
  const [autoEnabled, setAutoEnabled] = React.useState(() => {
    const stored = sessionStorage.getItem("autoNavEnabled");
    return stored === "true";
  });

  // Model profile — undefined means "use the default from API"
  const [modelProfile, setModelProfile] = React.useState<string | undefined>(
    undefined,
  );

  const { context, displayNames, handleContextDelete } = useRobotoLLMContext();

  // Chat ID state with sessionStorage persistence (sidebar use only).
  const [currentChatId, setCurrentChatId] = React.useState<string | undefined>(
    () => {
      if (initialChatId) {
        return initialChatId;
      }
      if (!ownsSessionStorage) {
        return undefined;
      }
      try {
        return sessionStorage.getItem(CHAT_ID_STORAGE_KEY) || undefined;
      } catch {
        return undefined;
      }
    },
  );

  // Persist chat ID to sessionStorage whenever it changes — but only when
  // this provider actually owns the storage slot.
  React.useEffect(() => {
    if (!ownsSessionStorage) {
      return;
    }
    if (currentChatId) {
      try {
        sessionStorage.setItem(CHAT_ID_STORAGE_KEY, currentChatId);
      } catch {
        // Ignore sessionStorage errors
      }
    } else {
      try {
        sessionStorage.removeItem(CHAT_ID_STORAGE_KEY);
      } catch {
        // Ignore sessionStorage errors
      }
    }
  }, [currentChatId, ownsSessionStorage]);

  // Route ``onChatIdChanged`` through a ref so an inline arrow at the call
  // site doesn't restart the effect every render. The effect fires only
  // when the chat id actually changes, and always calls the latest handler.
  const onChatIdChangedRef = React.useRef(onChatIdChanged);
  React.useEffect(() => {
    onChatIdChangedRef.current = onChatIdChanged;
  }, [onChatIdChanged]);
  React.useEffect(() => {
    onChatIdChangedRef.current?.(currentChatId);
  }, [currentChatId]);

  // UI State Handlers
  const handleNewChat = () => {
    setCurrentChatId(undefined);
    setShowHistory(false);
    setModelProfile(undefined);
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const handleToggleHistory = () => {
    setShowHistory((prev) => !prev);
  };

  const handleToggleAuto = () => {
    setAutoEnabled((prev) => {
      const newValue = !prev;
      sessionStorage.setItem("autoNavEnabled", String(newValue));
      return newValue;
    });
  };

  const handleHistoryChatClicked = (chatId: string) => {
    setCurrentChatId(chatId);
    setShowHistory(false);
  };

  const handleLinkClick = () => {
    setIsFullscreen(false);
  };

  const handleResetUIState = () => {
    setShowHistory(false);
    setIsFullscreen(false);
  };

  const value: AIChatContextValue = {
    // UI state
    isFullscreen,
    showHistory,
    autoEnabled,
    currentChatId,
    context,
    contextDisplayNames: displayNames,
    modelProfile,

    // UI state setters
    setIsFullscreen,
    setShowHistory,
    setAutoEnabled,
    setCurrentChatId,
    setModelProfile,

    // Convenience handlers
    handleContextDelete,
    handleNewChat,
    handleToggleFullscreen,
    handleToggleHistory,
    handleToggleAuto,
    handleHistoryChatClicked,
    handleLinkClick,
    handleResetUIState,
  };

  return (
    <AIChatContext.Provider value={value}>{children}</AIChatContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAIChat = (): AIChatContextValue => {
  const context = React.useContext(AIChatContext);
  if (context === undefined) {
    throw new Error("useAIChat must be used within an AIChatProvider");
  }
  return context;
};
